import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Search, X, Loader2, UserPlus, UserMinus } from 'lucide-react';
import { communitiesAPI } from '@/api/community';
import { useToast } from '@/hooks/use-toast';
import { getUserData, getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import axios from 'axios';

export default function CommunitiesList({ isOpen, onClose, isAdmin = false, isTeacher = false }) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [grades, setGrades] = useState([]);
  const [divisionsByGrade, setDivisionsByGrade] = useState({});
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [newCommunity, setNewCommunity] = useState({
    name: '',
    description: '',
    type: 'public',
    create_announcement: false,
    announcement_audience: 'school',
    announcement_grade_ids: [],
    announcement_division_ids: [],
  });
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [communityMembers, setCommunityMembers] = useState({});
  const [loadingMembers, setLoadingMembers] = useState({});
  const { toast } = useToast();
  const userData = getUserData('student') || getUserData('teacher') || getUserData('admin') || getUserData('guardian');
  const userType = userData?.user_type || 'student';
  const schoolId = userData?.school_id;

  useEffect(() => {
    if (isOpen) {
      loadCommunities();
      if (isAdmin) {
        loadGrades();
      }
    }
  }, [isOpen, isAdmin]);

  // No need for separate useEffect - divisions are loaded with grades

  const loadCommunities = async () => {
    try {
      setLoading(true);
      const response = await communitiesAPI.getCommunities();
      setCommunities(response.results || response || []);
    } catch (error) {
      console.error('Error loading communities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load communities',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadGrades = async () => {
    try {
      setLoadingGrades(true);
      const token = getAuthToken('admin') || getAuthToken('teacher') || getAuthToken('student') || getAuthToken('guardian');
      const response = await axios.get(apiUrl('community/school-grades-divisions/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setGrades(response.data.grades || []);
      setDivisionsByGrade(response.data.divisions || {});
    } catch (error) {
      console.error('Error loading grades and divisions:', error);
      toast({
        title: 'Warning',
        description: 'Could not load grades and divisions. You can still create a school-wide community.',
        variant: 'default',
      });
    } finally {
      setLoadingGrades(false);
    }
  };

  const handleJoin = async (communityId) => {
    try {
      await communitiesAPI.joinCommunity(communityId);
      toast({
        title: 'Success',
        description: 'Successfully joined community',
      });
      loadCommunities();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to join community',
        variant: 'destructive',
      });
    }
  };

  const handleLeave = async (communityId) => {
    try {
      await communitiesAPI.leaveCommunity(communityId);
      toast({
        title: 'Success',
        description: 'Successfully left community',
      });
      loadCommunities();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to leave community',
        variant: 'destructive',
      });
    }
  };

  const handleSearchUsers = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingUsers(true);
      const response = await communitiesAPI.searchUsers(query);
      setSearchResults(response.results || response || []);
    } catch (error) {
      console.error('Error searching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to search users',
        variant: 'destructive',
      });
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAddMember = async (communityId, username) => {
    try {
      setAddingMember(true);
      await communitiesAPI.addMember(communityId, null, username);
      toast({
        title: 'Success',
        description: 'User added to community successfully',
      });
      setMemberSearchTerm('');
      setSearchResults([]);
      loadCommunities();
      // Reload members if already loaded
      if (communityMembers[communityId]) {
        loadCommunityMembers(communityId);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to add user to community',
        variant: 'destructive',
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (communityId, userId) => {
    try {
      await communitiesAPI.removeMember(communityId, userId);
      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });
      loadCommunities();
      // Reload members
      if (communityMembers[communityId]) {
        loadCommunityMembers(communityId);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to remove member',
        variant: 'destructive',
      });
    }
  };

  const loadCommunityMembers = async (communityId) => {
    if (loadingMembers[communityId]) return;
    
    try {
      setLoadingMembers(prev => ({ ...prev, [communityId]: true }));
      const response = await communitiesAPI.getCommunityMembers(communityId);
      setCommunityMembers(prev => ({
        ...prev,
        [communityId]: response.results || response || []
      }));
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load community members',
        variant: 'destructive',
      });
    } finally {
      setLoadingMembers(prev => ({ ...prev, [communityId]: false }));
    }
  };

  const handleManageMembers = (community) => {
    setSelectedCommunity(community);
    setShowAddMember(true);
    if (!communityMembers[community.id]) {
      loadCommunityMembers(community.id);
    }
  };

  const handleCreate = async () => {
    if (!newCommunity.name.trim()) {
      toast({
        title: 'Error',
        description: 'Community name is required',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      // Prepare community data for API
      const communityData = {
        name: newCommunity.name,
        description: newCommunity.description,
        type: newCommunity.type,
      };
      
      // Add announcement options if enabled
      if (newCommunity.create_announcement) {
        communityData.create_announcement = true;
        communityData.announcement_audience = newCommunity.announcement_audience;
        if (newCommunity.announcement_grade_ids && newCommunity.announcement_grade_ids.length > 0) {
          communityData.announcement_grade_ids = newCommunity.announcement_grade_ids;
        }
        if (newCommunity.announcement_division_ids && newCommunity.announcement_division_ids.length > 0) {
          communityData.announcement_division_ids = newCommunity.announcement_division_ids;
        }
      }
      
      await communitiesAPI.createCommunity(communityData);
      toast({
        title: 'Success',
        description: 'Community created successfully' + (newCommunity.create_announcement ? ' with announcement invitation' : ''),
      });
      setNewCommunity({ 
        name: '', 
        description: '', 
        type: 'public',
        create_announcement: false,
        announcement_audience: 'school',
        announcement_grade_ids: [],
        announcement_division_ids: [],
      });
      setShowCreateForm(false);
      loadCommunities();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create community',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const filteredCommunities = communities.filter(community =>
    community.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    community.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--card-bg))] text-foreground [&>button]:text-foreground [&>button:hover]:bg-[hsl(var(--muted))]">
        <DialogHeader>
          <DialogTitle className="text-foreground">Communities</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search and Create */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="Search communities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground"
              />
            </div>
            {isAdmin && (
              <Button 
                onClick={() => setShowCreateForm(true)}
                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-[hsl(var(--primary-foreground))]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Community
              </Button>
            )}
          </div>

          {/* Create Form */}
          {showCreateForm && (
            <Card className="p-4 space-y-4 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Create New Community</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateForm(false)}
                  className="text-foreground hover:bg-[hsl(var(--muted))]"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-foreground">Name *</Label>
                  <Input
                    value={newCommunity.name}
                    onChange={(e) => setNewCommunity({ ...newCommunity, name: e.target.value })}
                    placeholder="Community name"
                    className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Description</Label>
                  <Textarea
                    value={newCommunity.description}
                    onChange={(e) => setNewCommunity({ ...newCommunity, description: e.target.value })}
                    placeholder="Community description"
                    rows={3}
                    className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Type</Label>
                  <Select
                    value={newCommunity.type}
                    onValueChange={(value) => setNewCommunity({ ...newCommunity, type: value })}
                  >
                    <SelectTrigger className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--popover-bg))] border-[hsl(var(--card-border))]">
                      <SelectItem value="public" className="text-foreground hover:bg-[hsl(var(--muted))]">Public (Anyone can join)</SelectItem>
                      <SelectItem value="private" className="text-foreground hover:bg-[hsl(var(--muted))]">Private (Invite only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Announcement Options (only for admins creating communities) */}
                {isAdmin && (
                  <>
                    <div className="flex items-center space-x-2 pt-2">
                      <input
                        type="checkbox"
                        id="create_announcement"
                        checked={newCommunity.create_announcement}
                        onChange={(e) => setNewCommunity({ ...newCommunity, create_announcement: e.target.checked })}
                        className="h-4 w-4 rounded border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]"
                      />
                      <Label htmlFor="create_announcement" className="font-normal cursor-pointer text-foreground">
                        Create announcement invitation
                      </Label>
                    </div>
                    
                    {newCommunity.create_announcement && (
                      <div className="space-y-3 pl-6 border-l-2 border-muted">
                        <div>
                          <Label className="text-foreground">Announcement Audience</Label>
                          <Select
                            value={newCommunity.announcement_audience}
                            onValueChange={(value) => setNewCommunity({ 
                              ...newCommunity, 
                              announcement_audience: value,
                              announcement_grade_ids: [],
                              announcement_division_ids: []
                            })}
                          >
                            <SelectTrigger className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[hsl(var(--popover-bg))] border-[hsl(var(--card-border))]">
                              <SelectItem value="school" className="text-foreground hover:bg-[hsl(var(--muted))]">Entire School</SelectItem>
                              <SelectItem value="grade" className="text-foreground hover:bg-[hsl(var(--muted))]">Specific Grade</SelectItem>
                              <SelectItem value="division" className="text-foreground hover:bg-[hsl(var(--muted))]">Specific Division</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {(newCommunity.announcement_audience === 'grade' || newCommunity.announcement_audience === 'division') && (
                          <>
                            <div>
                              <Label className="text-foreground">Select Grades {newCommunity.announcement_audience === 'grade' && '(Multiple)'}</Label>
                              {loadingGrades ? (
                                <div className="text-sm text-[hsl(var(--muted-foreground))] py-2">Loading grades...</div>
                              ) : (
                                <div className="space-y-2 max-h-48 overflow-y-auto border-[hsl(var(--card-border))] rounded-md p-3 bg-[hsl(var(--main-bg))]">
                                  {grades.length === 0 ? (
                                    <div className="text-sm text-[hsl(var(--muted-foreground))]">No grades available</div>
                                  ) : (
                                    grades.map((grade) => {
                                      const isSelected = newCommunity.announcement_grade_ids.includes(grade.id);
                                      return (
                                        <div key={grade.id} className="flex items-center space-x-2">
                                          <input
                                            type="checkbox"
                                            id={`grade-${grade.id}`}
                                            checked={isSelected}
                                            onChange={(e) => {
                                              const gradeIds = e.target.checked
                                                ? [...newCommunity.announcement_grade_ids, grade.id]
                                                : newCommunity.announcement_grade_ids.filter(id => id !== grade.id);
                                              
                                              // If division audience, remove divisions from unselected grades
                                              let divisionIds = newCommunity.announcement_division_ids;
                                              if (newCommunity.announcement_audience === 'division' && !e.target.checked) {
                                                const divisionsForGrade = divisionsByGrade[grade.id] || [];
                                                const divisionIdsForGrade = divisionsForGrade.map(d => d.id);
                                                divisionIds = divisionIds.filter(id => !divisionIdsForGrade.includes(id));
                                              }
                                              
                                              setNewCommunity({
                                                ...newCommunity,
                                                announcement_grade_ids: gradeIds,
                                                announcement_division_ids: divisionIds
                                              });
                                            }}
                                            className="h-4 w-4 rounded border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]"
                                          />
                                          <Label htmlFor={`grade-${grade.id}`} className="font-normal cursor-pointer flex-1 text-foreground">
                                            {grade.grade_name || `Grade ${grade.grade_number}`}
                                          </Label>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                            </div>
                            {newCommunity.announcement_audience === 'division' && newCommunity.announcement_grade_ids.length > 0 && (
                              <div>
                                <Label className="text-foreground">Select Divisions (Multiple)</Label>
                                <div className="space-y-3 max-h-64 overflow-y-auto border-[hsl(var(--card-border))] rounded-md p-3 bg-[hsl(var(--main-bg))]">
                                  {newCommunity.announcement_grade_ids.map((gradeId) => {
                                    const grade = grades.find(g => g.id === gradeId);
                                    const divisionsForGrade = divisionsByGrade[gradeId] || [];
                                    if (divisionsForGrade.length === 0) return null;
                                    
                                    return (
                                      <div key={gradeId} className="space-y-2">
                                        <div className="font-medium text-sm text-muted-foreground">
                                          {grade?.grade_name || `Grade ${grade?.grade_number}`}
                                        </div>
                                        <div className="pl-4 space-y-1">
                                          {divisionsForGrade.map((division) => {
                                            const isSelected = newCommunity.announcement_division_ids.includes(division.id);
                                            return (
                                              <div key={division.id} className="flex items-center space-x-2">
                                                <input
                                                  type="checkbox"
                                                  id={`division-${division.id}`}
                                                  checked={isSelected}
                                                  onChange={(e) => {
                                                    const divisionIds = e.target.checked
                                                      ? [...newCommunity.announcement_division_ids, division.id]
                                                      : newCommunity.announcement_division_ids.filter(id => id !== division.id);
                                                    setNewCommunity({
                                                      ...newCommunity,
                                                      announcement_division_ids: divisionIds
                                                    });
                                                  }}
                                                  className="h-4 w-4 rounded border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]"
                                                />
                                                <Label htmlFor={`division-${division.id}`} className="font-normal cursor-pointer flex-1 text-foreground">
                                                  {division.division_name}
                                                </Label>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {newCommunity.announcement_grade_ids.every(gradeId => (divisionsByGrade[gradeId] || []).length === 0) && (
                                    <div className="text-sm text-[hsl(var(--muted-foreground))]">No divisions available for selected grades</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
                
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCreateForm(false)}
                    className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--muted))]"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreate} 
                    disabled={creating}
                    className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-[hsl(var(--primary-foreground))]"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create'
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Communities List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--green-bright))]" />
            </div>
          ) : filteredCommunities.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No communities found
            </div>
          ) : (
            <div className="space-y-4">
              {/* Joined Communities Section */}
              {filteredCommunities.filter(c => c.is_member).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-foreground">My Communities</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {filteredCommunities.filter(c => c.is_member).map((community) => (
                      <Card key={community.id} className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Users className="w-5 h-5 text-[hsl(var(--green-bright))]" />
                              <h3 className="font-semibold text-foreground">{community.name}</h3>
                            </div>
                            <Badge variant={community.type === 'public' ? 'default' : 'secondary'} className="bg-[hsl(var(--muted))] text-foreground">
                              {community.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-2">
                            {community.description || 'No description'}
                          </p>
                          <div className="flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
                            <span>{community.member_count || 0} members</span>
                            <div className="flex gap-2">
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleManageMembers(community)}
                                  className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))] border-[hsl(var(--card-border))] hover:bg-[hsl(var(--muted))]"
                                >
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  Manage
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleLeave(community.id)}
                                className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--muted))]"
                              >
                                Leave
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Available Communities Section */}
              {filteredCommunities.filter(c => !c.is_member).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-foreground">
                    {filteredCommunities.filter(c => c.is_member).length > 0 ? 'Available Communities' : 'Communities'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredCommunities.filter(c => !c.is_member).map((community) => (
                      <Card key={community.id} className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Users className="w-5 h-5 text-[hsl(var(--green-bright))]" />
                              <h3 className="font-semibold text-foreground">{community.name}</h3>
                            </div>
                            <Badge variant={community.type === 'public' ? 'default' : 'secondary'} className="bg-[hsl(var(--muted))] text-foreground">
                              {community.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-2">
                            {community.description || 'No description'}
                          </p>
                          <div className="flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
                            <span>{community.member_count || 0} members</span>
                            <div className="flex gap-2">
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleManageMembers(community)}
                                  className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))] border-[hsl(var(--card-border))] hover:bg-[hsl(var(--muted))]"
                                >
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  Manage
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => handleJoin(community.id)}
                                disabled={community.type === 'private' && !isAdmin}
                                className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))]"
                              >
                                {community.type === 'private' && !isAdmin ? 'Private' : 'Join'}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empty State */}
              {filteredCommunities.length === 0 && (
                <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">
                  No communities found
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Add Member Dialog */}
      {showAddMember && selectedCommunity && (
        <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--card-bg))] text-foreground [&>button]:text-foreground [&>button:hover]:bg-[hsl(var(--muted))]">
            <DialogHeader>
              <DialogTitle className="text-foreground">Manage Members - {selectedCommunity.name}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Add Member Section */}
              <div className="space-y-2">
                <Label className="text-foreground">Add Member by Username</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      placeholder="Search by username..."
                      value={memberSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setMemberSearchTerm(value);
                        handleSearchUsers(value);
                      }}
                      className="pl-10 bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      if (memberSearchTerm.trim()) {
                        handleAddMember(selectedCommunity.id, memberSearchTerm.trim());
                      }
                    }}
                    disabled={!memberSearchTerm.trim() || addingMember}
                    className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-[hsl(var(--primary-foreground))]"
                  >
                    {addingMember ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add
                      </>
                    )}
                  </Button>
                </div>

                {/* Search Results */}
                {searchingUsers && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--green-bright))]" />
                  </div>
                )}

                {!searchingUsers && searchResults.length > 0 && (
                  <div className="border-[hsl(var(--card-border))] rounded-md max-h-48 overflow-y-auto bg-[hsl(var(--main-bg))]">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 border-b-[hsl(var(--card-border))] last:border-b-0 hover:bg-[hsl(var(--muted))] cursor-pointer"
                        onClick={() => handleAddMember(selectedCommunity.id, user.username)}
                      >
                        <div>
                          <div className="font-medium text-foreground">{user.full_name || user.username}</div>
                          <div className="text-sm text-[hsl(var(--muted-foreground))]">
                            {user.username} • {user.user_type}
                            {user.grade && ` • ${user.grade}`}
                            {user.division && ` • ${user.division}`}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddMember(selectedCommunity.id, user.username);
                          }}
                          disabled={addingMember}
                          className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--muted))]"
                        >
                          <UserPlus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Current Members Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Current Members ({communityMembers[selectedCommunity.id]?.length || 0})</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => loadCommunityMembers(selectedCommunity.id)}
                    disabled={loadingMembers[selectedCommunity.id]}
                    className="text-foreground hover:bg-[hsl(var(--muted))]"
                  >
                    {loadingMembers[selectedCommunity.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Refresh'
                    )}
                  </Button>
                </div>

                {loadingMembers[selectedCommunity.id] ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--green-bright))]" />
                  </div>
                ) : communityMembers[selectedCommunity.id]?.length > 0 ? (
                  <div className="border-[hsl(var(--card-border))] rounded-md max-h-64 overflow-y-auto bg-[hsl(var(--main-bg))]">
                    {communityMembers[selectedCommunity.id].map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 border-b-[hsl(var(--card-border))] last:border-b-0"
                      >
                        <div>
                          <div className="font-medium text-foreground">
                            {member.user?.full_name || member.user?.username || 'Unknown'}
                          </div>
                          <div className="text-sm text-[hsl(var(--muted-foreground))]">
                            {member.user?.username}
                            {member.joined_at && ` • Joined ${new Date(member.joined_at).toLocaleDateString()}`}
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRemoveMember(selectedCommunity.id, member.user?.id)}
                            className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--muted))]"
                          >
                            <UserMinus className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                    No members yet
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
